'use client'

import { useRouter } from 'next/navigation'
import { useState, useEffect, useRef } from 'react'
import { buildUrl } from '../helpers'
import { sortFields } from './helpers'

export const SortMenu = ({
  currentBrand,
  currentSortSlug,
  showAll,
  inStockFilter,
}: SortMenuProps) => {
  const router = useRouter()
  const [isChanging, setIsChanging] = useState(false)
  const previousSortRef = useRef(currentSortSlug)
  const previousShowAllRef = useRef(showAll)

  // Reset loading state when props change (navigation completed)
  useEffect(() => {
    if (
      previousSortRef.current !== currentSortSlug ||
      previousShowAllRef.current !== showAll
    ) {
      previousSortRef.current = currentSortSlug
      previousShowAllRef.current = showAll
      // Defer state update to avoid synchronous setState in effect
      queueMicrotask(() => {
        setIsChanging(false)
      })
    }
  }, [currentSortSlug, showAll]) // Also reset when showAll changes

  return (
    <div className="w-full">
      <label
        htmlFor="sort-menu"
        className="block text-sm font-medium text-gray-700 mb-2"
      >
        Sort by
      </label>
      <div className="flex items-center gap-4">
        <div className="relative flex-1">
          <select
            id="sort-menu"
            className={`w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-vi-red focus:border-vi-red bg-white hover:bg-gray-50 transition-all duration-200 cursor-pointer ${
              isChanging ? 'opacity-60 pointer-events-none' : ''
            }`}
            value={
              sortFields.find(f => f.slug === currentSortSlug)?.slug ||
              currentSortSlug
            }
            onChange={e => {
              const sort = e.target.value
              if (sort === currentSortSlug) return // Don't navigate if same sort
              setIsChanging(true)
              const newUrl = buildUrl(currentBrand, sort, showAll, 1) // Reset to page 1 when changing sort
              router.push(newUrl)
            }}
            disabled={isChanging}
          >
            {sortFields.map(option => (
              <option key={option.slug} value={option.slug}>
                {option.name}
              </option>
            ))}
          </select>
          {isChanging && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-vi-red border-t-transparent" />
            </div>
          )}
        </div>
        {inStockFilter}
      </div>
    </div>
  )
}

interface SortMenuProps {
  currentBrand: string
  currentSortSlug: string
  showAll: boolean
  inStockFilter: React.ReactNode
}
